import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/ui/Sidebar';
import TopBar from '../../components/ui/TopBar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import { useSidebar } from '../../contexts/SidebarContext';
import productionService from '../../services/productionService';
import PackageTrackingCard from './components/PackageTrackingCard';
import DeliveryTimeline from './components/DeliveryTimeline';
import QRPackageScanner from './components/QRPackageScanner';
import CommunicationPanel from './components/CommunicationPanel';
import DeliveryScheduler from './components/DeliveryScheduler';
import AlertNotificationPanel from './components/AlertNotificationPanel';

const ClientDeliveryTrackingPortal = () => {
  const navigate = useNavigate();
  const { isCollapsed, toggleSidebar } = useSidebar();
  const [activeView, setActiveView] = useState('tracking');
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [scannedQR, setScannedQR] = useState(null);

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/dashboard', icon: 'LayoutDashboard' },
    { label: 'Suivi Livraison Client', path: '/client-delivery-tracking-portal', icon: 'Package' }
  ];

  useEffect(() => {
    fetchClientPackages();
    checkDeliveryAlerts();
  }, []);

  const fetchClientPackages = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch completed batches ready for delivery
      const data = await productionService?.getAll({ status: 'completed' });
      
      // Transform to package format with delivery tracking
      const packagesData = data?.map(batch => ({
        id: batch?.id,
        packageNumber: `PKG-${batch?.batchNumber}`,
        batchNumber: batch?.batchNumber,
        patientName: batch?.patientName,
        clinicName: batch?.clinicName,
        status: 'in_transit',
        trackingNumber: `TRK-${Date.now()}-${batch?.id?.slice(0, 8)}`,
        estimatedDelivery: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000)?.toISOString(),
        currentLocation: 'Centre de distribution',
        carrier: 'DHL Express',
        qrCode: `DELIVERY-${batch?.batchNumber}-${Date.now()}`,
        completionDate: batch?.completionDate,
        deliveryHistory: [
          { status: 'Production terminée', location: 'Usine ClearMotion', timestamp: batch?.completionDate },
          { status: 'Emballage', location: 'Usine ClearMotion', timestamp: new Date(batch?.completionDate)?.getTime() + 2 * 60 * 60 * 1000 },
          { status: 'En transit', location: 'Centre de distribution', timestamp: new Date(batch?.completionDate)?.getTime() + 24 * 60 * 60 * 1000 }
        ]
      })) || [];
      
      setPackages(packagesData);
    } catch (err) {
      console.error('Error fetching packages:', err);
      setError('Erreur lors du chargement des colis');
      setPackages([]);
    } finally {
      setLoading(false);
    }
  };

  const checkDeliveryAlerts = () => {
    const now = new Date();
    const newAlerts = [];

    packages?.forEach(pkg => {
      const estimatedDelivery = new Date(pkg?.estimatedDelivery);
      const hoursUntilDelivery = (estimatedDelivery - now) / (1000 * 60 * 60);

      // 24-hour delivery alert
      if (hoursUntilDelivery <= 24 && hoursUntilDelivery > 0) {
        newAlerts?.push({
          id: `alert-delivery-${pkg?.id}`,
          type: 'info',
          packageNumber: pkg?.packageNumber,
          title: 'Livraison imminente',
          message: `Votre colis ${pkg?.packageNumber} sera livré dans moins de 24 heures`,
          timestamp: now
        });
      }

      // 10-day advance shipping notice
      const daysSinceCompletion = (now - new Date(pkg?.completionDate)) / (1000 * 60 * 60 * 24);
      if (daysSinceCompletion >= 10 && pkg?.status !== 'delivered') {
        newAlerts?.push({
          id: `alert-shipping-${pkg?.id}`,
          type: 'warning',
          packageNumber: pkg?.packageNumber,
          title: 'Délai d\'expédition',
          message: `Le colis ${pkg?.packageNumber} est en transit depuis plus de 10 jours`,
          timestamp: now
        });
      }
    });

    setAlerts(newAlerts);
  };

  const handleQRScan = (qrData) => {
    setScannedQR(qrData);
    
    // Find package by QR code
    const foundPackage = packages?.find(pkg => pkg?.qrCode === qrData);
    if (foundPackage) {
      setSelectedPackage(foundPackage);
      setActiveView('tracking');
    }
  };

  const handlePackageSelect = (pkg) => {
    setSelectedPackage(pkg);
    setActiveView('tracking');
  };

  const viewTabs = [
    { id: 'tracking', label: 'Suivi Colis', icon: 'Package' },
    { id: 'scanner', label: 'Scanner QR', icon: 'QrCode' },
    { id: 'schedule', label: 'Planifier Livraison', icon: 'Calendar' },
    { id: 'communication', label: 'Messages', icon: 'MessageSquare' }
  ];

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <Sidebar onToggle={toggleSidebar} />
      <div className="flex-1 flex flex-col overflow-hidden">
           <TopBar sidebarCollapsed={isCollapsed} onSidebarToggle={toggleSidebar} />

        <div className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
          <div className="container mx-auto px-4 py-6 max-w-7xl">
            <Breadcrumb items={breadcrumbItems} />

            {/* Header */}
            <div className="mb-6">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Portail de Suivi Livraison Client</h1>
              <p className="text-gray-600">Suivez vos colis en temps réel avec traçabilité QR code</p>
            </div>

            {/* Alerts */}
            {alerts?.length > 0 && (
              <AlertNotificationPanel alerts={alerts} onPackageSelect={handlePackageSelect} packages={packages} />
            )}

            {/* View Tabs */}
            <div className="bg-white border border-gray-200 rounded-lg p-2 mb-6">
              <div className="flex flex-wrap gap-2">
                {viewTabs?.map(tab => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveView(tab?.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      activeView === tab?.id
                        ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <span>{tab?.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Content */}
            {loading ? (
              <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
                <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-gray-600">Chargement des colis...</p>
              </div>
            ) : error ? (
              <div className="bg-error/10 border border-error/30 rounded-lg p-6 text-center">
                <p className="text-error font-medium">{error}</p>
                <Button variant="outline" onClick={fetchClientPackages} className="mt-4">
                  Réessayer
                </Button>
              </div>
            ) : (
              <>
                {activeView === 'tracking' && (
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Package List */}
                    <div className="lg:col-span-1">
                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Mes Colis</h3>
                        <div className="space-y-3 max-h-[600px] overflow-y-auto">
                          {packages?.map(pkg => (
                            <PackageTrackingCard
                              key={pkg?.id}
                              package={pkg}
                              isSelected={selectedPackage?.id === pkg?.id}
                              onClick={() => handlePackageSelect(pkg)}
                            />
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Delivery Timeline */}
                    <div className="lg:col-span-2">
                      {selectedPackage ? (
                        <DeliveryTimeline package={selectedPackage} />
                      ) : (
                        <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
                          <div className="text-gray-300 mb-4">📦</div>
                          <h3 className="text-lg font-medium text-gray-900 mb-2">Sélectionnez un colis</h3>
                          <p className="text-sm text-gray-600">Choisissez un colis pour voir les détails de livraison</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {activeView === 'scanner' && (
                  <QRPackageScanner onScan={handleQRScan} scannedData={scannedQR} />
                )}

                {activeView === 'schedule' && (
                  <DeliveryScheduler packages={packages} onScheduleUpdate={fetchClientPackages} />
                )}

                {activeView === 'communication' && (
                  <CommunicationPanel selectedPackage={selectedPackage} />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientDeliveryTrackingPortal;